import useSWR from "swr";
import api from "./api";
import { createFetcher } from "./fetcher";
import { Customer } from "../interfaces/customer";
import { alertSuccess, alertError } from "../utils/alert";
import { APIWithPagination } from "../interfaces/pagination";

export const useAllCustomers = ({
  page = 0,
  pageSize = 25,
  search,
  ordering,
}: APIWithPagination & { isTenant?: boolean }) => {
  let url = `/customers/`;

  url += `?page=${page + 1}&page_size=${pageSize}`;

  if (search !== undefined) {
    url += `&search=${search}`;
  }

  if (ordering !== undefined) {
    url += `&ordering=${ordering}`;
  }

  const snapshot = useSWR<{
    count: number;
    next: string;
    previous: string;
    results: Customer[];
    total_pages: number;
  }>(url, createFetcher, {
    revalidateOnMount: true,
  });

  return { ...snapshot };
};

export const addCustomers = async ({
  customers,
  onSuccess,
  setAlertInfo,
}: {
  customers: Customer[];
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/customers/create`;

  try {
    await api.post(url, customers);

    alertSuccess(`Customer${customers.length > 1 ? "s" : ""} added!`, setAlertInfo);
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data?.[0].email?.[0] ??
            error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};

export const updateCustomer = async ({
  customerId,
  body,
  onSuccess,
  setAlertInfo,
}: {
  customerId: Customer;
  body: Partial<Customer>;
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/customers/${customerId}/update`;

  try {
    await api.patch(url, body);

    alertSuccess(
      `User "${body.name || body.contract}" updated!`,
      setAlertInfo
    );
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};

export const deleteCustomer = async ({
  customer,
  onSuccess,
  setAlertInfo,
}: {
  customer: Customer;
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/customers/${customer.id}/delete`;

  try {
    await api.delete(url);

    alertSuccess(`Customer deleted!`, setAlertInfo);
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};

export const useCustomerInfo = (customerId: string | number | undefined) => {
  let url = `/customers/${customerId}`;

  const snapshot = useSWR<Storage>(
    () => {
      if (customerId === -1) return;
      return url;
    },
    createFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { ...snapshot };
};
